import { useEffect, useRef, useState } from "react";

/**
 * Animated count-up number. Counts from 0 to `end` over `duration` ms when
 * the element enters the viewport. Respects prefers-reduced-motion.
 */
export default function CountUp({ end, duration = 1600, suffix = "", prefix = "", className = "", "data-testid": dataTestId }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (typeof end !== "number" || end <= 0) { setVal(end || 0); return; }
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setVal(end);
      return;
    }
    const node = ref.current;
    if (!node) return;

    const startAnim = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const startTime = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - startTime) / duration);
        // easeOutExpo
        const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
        setVal(Math.round(eased * end));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) startAnim(); });
    }, { threshold: 0.3 });
    io.observe(node);
    return () => io.disconnect();
  }, [end, duration]);

  return (
    <span ref={ref} className={className} data-testid={dataTestId} aria-label={`${prefix}${end}${suffix}`}>
      {prefix}{val.toLocaleString("fr-FR")}{suffix}
    </span>
  );
}
